import { Component, computed, effect, input, linkedSignal, output, OutputEmitterRef, signal } from '@angular/core';
import { MatMenu } from '@angular/material/menu';

import { CropFieldComparator, SortMethodType } from './helpers/sort.helpers';
import { AdviceMouseClickEvent, CropFieldAdvice } from './models/models';
import { TableComponent } from '../widget-components/table/table.component';
import { OverviewTableToolbarComponent } from './components/table-toolbar.component';
import { OverviewTableHeaderComponent } from './components/table-header.component';
import { OverviewTableRowComponent } from './components/table-row.component';
import { IncludesPipe } from './pipes/includes.pipe';

@Component({
  selector: 'app-disease-overview-table',
  styleUrl: 'table.component.scss',
  template: `
    <div class="table">
      <app-disease-overview-table-toolbar
        [diseaseCropFields]="diseaseCropFields()"
        [minimalDate]="minimalDate()"
        [selectedDates]="activeSelectedDates()"
        [cropYear]="cropYear()"
        [selectedCropFields]="selectedCropFields()"
        [actionMenu]="actionMenu()"
        [availableSortMethods]="availableSortMethods()"
        (selectedDatesChanges)="onDatesChange($event)"
        (sortMethodChanges)="onSortMethodChange($event)"
        (actionMenuOpened)="onActionMenuOpened()"
        (cropFieldVisibilityChanged)="onCropFieldVisibilityChanged($event)"
        (selectedCropFieldsChanged)="onSelectedCropFieldsChanged($event)"
      />
      <app-disease-overview-table-header
        [selectedDates]="activeSelectedDates()"
        [allSelected]="areAllSelected()"
        (selectAllClicked)="onSelectAllChange($event)"
      />
      @for (advice of sortedAdvices(); track advice.cropFieldId; let last = $last) {
        <app-disease-overview-table-row
          class="table-row"
          [isLastRow]="last"
          [advice]="advice"
          [selectedDates]="activeSelectedDates()!"
          [selected]="selectedCropFields()! | includes: advice"
          (selectionChange)="onRowSelectionChange($event, advice)"
          (titleClicked)="onAdviceTitleClick($event)"
        />
      }
    </div>
  `,
  imports: [OverviewTableToolbarComponent, OverviewTableHeaderComponent, OverviewTableRowComponent, IncludesPipe],
})
export class DiseaseOverviewTableComponent extends TableComponent {
  protected readonly visibleCropFieldIds = signal<Set<number>>(new Set());
  protected readonly sortMethod = signal<CropFieldComparator | null>(null);
  protected readonly activeSelectedDates = linkedSignal(() => this.selectedDates());

  public readonly diseaseCropFields =
    input.required<{ id: number; name: string; areaInHectares: number; isVisible: boolean }[]>();
  public readonly selectedDates = input<Date[]>([]);
  public readonly cropYear = input<number | undefined>(undefined);
  public readonly minimalDate = input<Date | undefined>(undefined);
  public readonly actionMenu = input.required<MatMenu>();
  public readonly availableSortMethods = input<SortMethodType[]>([
    SortMethodType.Name,
    SortMethodType.Advice,
    SortMethodType.Spray,
  ]);

  public readonly cropFieldVisibilityChanged = output<{ cropFieldId: number; isVisible: boolean }>();
  public readonly selectedDatesChanges = output<Date[]>();
  public readonly sortMethodChanged = output<CropFieldComparator>();
  public readonly actionMenuOpened = output<void>();
  public readonly adviceTitleClick: OutputEmitterRef<AdviceMouseClickEvent> = output<AdviceMouseClickEvent>();

  constructor() {
    super();
    effect(() => {
      const visibleIds = new Set(
        this.diseaseCropFields()
          .filter((cf) => cf.isVisible)
          .map((cf) => cf.id),
      );
      this.visibleCropFieldIds.set(visibleIds);
    });
  }

  protected readonly visibleAdvices = computed(() =>
    this.advices().filter((advice) => this.visibleCropFieldIds().has(advice.cropFieldId)),
  );

  protected readonly sortedAdvices = linkedSignal<
    { cropYear: number | undefined; visible: CropFieldAdvice[] },
    CropFieldAdvice[]
  >({
    source: () => ({
      cropYear: this.cropYear(),
      visible: this.visibleAdvices(),
    }),
    computation: ({ cropYear, visible }, previous) => {
      const sortMethod = this.sortMethod();
      if (visible.length === 0 && previous?.value && previous.source.cropYear === cropYear) {
        return previous.value;
      }
      return sortMethod ? [...visible].sort(sortMethod) : visible;
    },
  });

  protected readonly areAllSelected = computed(() => {
    const visible = this.visibleAdvices();
    return visible.length > 0 && this.selectedCropFields().length === visible.length;
  });

  protected onDatesChange(dates: Date[]): void {
    this.activeSelectedDates.set(dates);
    this.selectedDatesChanges.emit(dates);
  }

  protected onSortMethodChange(sortMethod: CropFieldComparator): void {
    this.sortMethod.set(sortMethod);
    this.sortMethodChanged.emit(sortMethod);
  }

  protected onActionMenuOpened(): void {
    this.actionMenuOpened.emit();
  }

  protected onSelectAllChange(isSelected: boolean): void {
    const updatedSelection = isSelected ? this.visibleAdvices() : [];
    this.selectedCropFields.set(updatedSelection);
    this.selectedCropFieldsChanged.emit(updatedSelection);
  }

  protected onAdviceTitleClick(event: AdviceMouseClickEvent): void {
    this.adviceTitleClick.emit(event);
  }

  protected onSelectedCropFieldsChanged(selected: CropFieldAdvice[]): void {
    this.selectedCropFields.set(selected);
    this.selectedCropFieldsChanged.emit(selected);
  }

  protected onCropFieldVisibilityChanged(event: { cropFieldId: number; isVisible: boolean }): void {
    const updated = new Set(this.visibleCropFieldIds());
    event.isVisible ? updated.add(event.cropFieldId) : updated.delete(event.cropFieldId);
    this.visibleCropFieldIds.set(updated);

    this.pruneSelectionToVisibleCropFields(updated);
    this.cropFieldVisibilityChanged.emit(event);
  }

  private pruneSelectionToVisibleCropFields(visibleIds: Set<number>): void {
    const pruned = this.selectedCropFields().filter((a) => visibleIds.has(a.cropFieldId));
    this.selectedCropFields.set(pruned);
    this.selectedCropFieldsChanged.emit(pruned);
  }
}
